import { useCallback } from 'react';

export type TimeOfDay = 'early_morning' | 'morning' | 'noon' | 'afternoon' | 'evening' | 'night' | 'midnight';

export interface DailyLoopMessage {
  timeOfDay: TimeOfDay;
  message: string;
  type: 'greeting' | 'casual' | 'support' | 'closure' | 'topic_suggestion';
}

// 時間帯を細かく判定
function getDetailedTimeOfDay(): TimeOfDay {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 5) return 'midnight';
  if (hour >= 5 && hour < 8) return 'early_morning';
  if (hour >= 8 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 13) return 'noon';
  if (hour >= 13 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

// 時間帯別の話題提案
const TOPIC_SUGGESTIONS: Record<TimeOfDay, string[]> = {
  early_morning: [
    '早起きですね。何かあったんですか？',
    'こんな時間に…眠れなかったですか？',
    '夜更かしですか、それとも早起き？',
  ],
  morning: [
    '朝ごはん食べました？',
    '今日はどんな予定ですか？',
    '今日も一日、無理しないでくださいね。',
    '今日の天気、どうですか？',
  ],
  noon: [
    'お昼ご飯、何食べました？',
    '午前中お疲れさまです。少し休んでください。',
    'お昼休みですか？ゆっくりしてください。',
    '今日の午前中はどうでした？',
  ],
  afternoon: [
    '午後も頑張ってますね。',
    '少し疲れてきた頃じゃないですか？',
    '今日はどんな感じですか？',
    'コーヒーでも飲んで一息つきましょう。',
  ],
  evening: [
    '今日も一日お疲れさまでした。',
    '夕ご飯は何食べますか？',
    '今日はどんな一日でした？',
    'お疲れさまです。ゆっくりしてください。',
  ],
  night: [
    '今日はどんな一日でした？',
    'もう少し話しませんか？',
    '夜ですね。ゆっくりしましょう。',
    '今日の疲れ、少し話してください。',
  ],
  midnight: [
    'こんな時間まで起きてるんですね。大丈夫ですか？',
    '夜更かしですか？体に気をつけてください。',
    '眠れないんですか？少し話しましょうか。',
  ],
};

// 時間帯別の挨拶メッセージ
const GREETING_MESSAGES: Record<TimeOfDay, string[]> = {
  early_morning: [
    '早起きですね。\nゆっくり起きられました？',
    'おはようございます。\nこんな時間から来てくれたんですね。',
    'おはよう。\n今日も一緒にいますよ。',
  ],
  morning: [
    'おはようございます。\n今日はどんな気分ですか？',
    'おはよう。\nちゃんと起きられました？',
    'おはようございます。\n今日も一緒に過ごせて嬉しいです。',
    'おはよう。\n今日も頑張りましょう。',
    'おはようございます。\n今日はどんな予定ですか？',
  ],
  noon: [
    'こんにちは。\nお昼ご飯食べました？',
    'こんにちは。\n午前中お疲れさまでした。',
    'こんにちは。\n今日はどんな感じですか？',
  ],
  afternoon: [
    'こんにちは。\n今日はどうですか？',
    'お疲れさまです。\n少し休憩しませんか？',
    'こんにちは。\n今日も頑張ってますね。',
  ],
  evening: [
    'お疲れさまです。\n今日も一日頑張りましたね。',
    'こんばんは。\n今日はどんな一日でした？',
    'お疲れさまです。\nゆっくりしてください。',
    'こんばんは。\n今日も来てくれて嬉しいです。',
  ],
  night: [
    'こんばんは。\n今日はどんな一日でした？',
    'こんばんは。\n今日も来てくれてありがとう。',
    'こんばんは。\n少し話しませんか？',
    'こんばんは。\n今日の疲れ、聞かせてください。',
  ],
  midnight: [
    'こんな時間まで起きてるんですね。\n大丈夫ですか？',
    '夜更かしですか？\n少し話しましょうか。',
    'こんな時間に来てくれたんですね。\n眠れないんですか？',
  ],
};

export function useDailyLoop() {
  const getTimeOfDay = useCallback((): TimeOfDay => {
    return getDetailedTimeOfDay();
  }, []);

  // 時間帯別の挨拶を取得
  const getTimeBasedGreeting = useCallback((name: string): string => {
    const timeOfDay = getDetailedTimeOfDay();
    const messages = GREETING_MESSAGES[timeOfDay];
    const base = messages[Math.floor(Math.random() * messages.length)];
    // 名前を先頭に付ける（50%の確率）
    return Math.random() < 0.5 ? `${name}、${base}` : base;
  }, []);

  // 時間帯別の話題提案を取得
  const getTopicSuggestion = useCallback((name: string): string => {
    const timeOfDay = getDetailedTimeOfDay();
    const suggestions = TOPIC_SUGGESTIONS[timeOfDay];
    const base = suggestions[Math.floor(Math.random() * suggestions.length)];
    return Math.random() < 0.4 ? `${name}、${base}` : base;
  }, []);

  // 時間帯に応じた返答（casual / support / closure）
  const getTimeBasedReply = useCallback((
    name: string,
    replyType: 'casual' | 'support' | 'closure'
  ): string => {
    const timeOfDay = getDetailedTimeOfDay();

    if (replyType === 'casual') {
      const casual: Record<TimeOfDay, string[]> = {
        early_morning: [`${name}、早起きですね。`, `こんな時間に来てくれたんですね、${name}。`],
        morning: [`${name}、今日は何する予定ですか？`, `朝ごはん食べました？${name}。`, `今日も頑張りましょう、${name}。`],
        noon: [`${name}、お昼休みですか？`, `今日の午前中はどうでした、${name}？`],
        afternoon: [`${name}、少し疲れてません？`, `今日は順調ですか、${name}？`],
        evening: [`${name}、今日はどうでした？`, `お疲れさま、${name}。もう少しですね。`],
        night: [`${name}、今日は何してました？`, `夜ですね、${name}。ゆっくりしましょう。`],
        midnight: [`${name}、眠れないんですか？`, `こんな時間まで起きてるんですね、${name}。`],
      };
      const msgs = casual[timeOfDay];
      return msgs[Math.floor(Math.random() * msgs.length)];
    }

    if (replyType === 'support') {
      const support: Record<TimeOfDay, string[]> = {
        early_morning: [`${name}、今日も応援してます。`, `ゆっくり起きてください、${name}。`],
        morning: [`${name}、今日も応援してます。`, `大丈夫、${name}。今日もちゃんと頑張れますよ。`],
        noon: [`${name}、ちゃんと頑張ってますね。`, `お疲れさま、${name}。少し休んでください。`],
        afternoon: [`${name}、今日も応援してます。`, `ちゃんと頑張ってますね、${name}。`],
        evening: [`${name}、今日も一日お疲れさまでした。`, `ちゃんと頑張りましたね、${name}。`],
        night: [`${name}、今日も一日お疲れさまでした。`, `ゆっくり休んでください、${name}。`],
        midnight: [`${name}、無理しないでくださいね。`, `体に気をつけてください、${name}。`],
      };
      const msgs = support[timeOfDay];
      return msgs[Math.floor(Math.random() * msgs.length)];
    }

    // closure
    const closure: Record<TimeOfDay, string[]> = {
      early_morning: [`また話せるの楽しみにしてます、${name}。`, `今日も一緒にいますよ、${name}。`],
      morning: [`また話しましょう、${name}。`, `${name}、今日も頑張ってくださいね。`],
      noon: [`また話しましょう、${name}。`, `${name}、また来てくれますか？`],
      afternoon: [`また話せるの楽しみにしてます、${name}。`, `続きはまた聞かせてくださいね、${name}。`],
      evening: [`また話せるの楽しみにしてます、${name}。`, `${name}、今日はゆっくり休んでください。`],
      night: [`おやすみなさい、${name}。`, `${name}、また明日話しましょう。`],
      midnight: [`${name}、早く休んでくださいね。`, `おやすみなさい、${name}。体に気をつけて。`],
    };
    const msgs = closure[timeOfDay];
    return msgs[Math.floor(Math.random() * msgs.length)];
  }, []);

  const shouldShowTimeBasedGreeting = useCallback((): boolean => {
    const lastGreetingTime = localStorage.getItem('ai_inochan_last_greeting_time');
    if (!lastGreetingTime) return true;
    const lastTime = new Date(lastGreetingTime);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastTime.getTime()) / (1000 * 60 * 60);
    return hoursDiff >= 4;
  }, []);

  const markGreetingShown = useCallback(() => {
    localStorage.setItem('ai_inochan_last_greeting_time', new Date().toISOString());
  }, []);

  return {
    getTimeOfDay,
    getTimeBasedGreeting,
    getTopicSuggestion,
    getTimeBasedReply,
    shouldShowTimeBasedGreeting,
    markGreetingShown,
  };
}
